import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Button, Container } from 'react-bootstrap';
import Filter from '../components/Filter';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useCargo } from '../../../apis/cargo/CargoProvider';
import { useCargoSearchList } from '../../../apis/cargoSearchList/CargoSearchListProvider';
import { getPageName } from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
//import createIcon from "../../../assets/create-icon.png"; // Ajuste o caminho conforme necessário
import i18n from '../../../i18n';
import { useNavigate } from 'react-router-dom';
import NavBar, { NavBarItem } from '../../../components/NavBar';
import CargoTablePaginated from '../components/CargoTablePaginated';
import CargoContentList from '../../../components/CargoContentList';
import { Content } from '../../Release/types';
import { downloadContent } from '../../../services/contentService';
import AlertOverlay from '../../../components/Alert';

const CargoListView: React.FC = () => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState<{
    message: string;
    variant: 'success' | 'danger' | 'warning' | 'info';
  } | null>(null);
  const navigate = useNavigate();
  const { cargos, fetchCargos, apiResponse } = useCargo();
  const { cargosSearchList, fetchCargoSearchList } = useCargoSearchList();
  const [isReleased, setIsReleased] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 20,
    page: 0,
  });

  useEffect(() => {
    fetchCargoSearchList();
  }, [fetchCargoSearchList]);

  useEffect(() => {
    if (cargosSearchList && cargosSearchList.length > 0) {
      const initialFilters = Object.fromEntries(
        cargosSearchList.map(({ key }: { key: string }) => [key, ''])
      );
      initialFilters.pageNum = "0";
      initialFilters.quantityPerPage = "20";

      setFilters((prevFilters) => {
        if (JSON.stringify(prevFilters) !== JSON.stringify(initialFilters)) {
          return initialFilters;
        }
        return prevFilters;
      });
    }
  }, [cargosSearchList]);

  const dateFormatter = new Intl.DateTimeFormat(i18n?.language ?? 'pt');

  useEffect(() => {
    const newFilters = { 
      ...filters, 
      pageNum: paginationModel.page.toString(),
      quantityPerPage: paginationModel.pageSize.toString()
    };
    setFilters(newFilters);
    fetchCargos(newFilters, isReleased);
  }, [paginationModel]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    const newFilters = { 
      ...filters, 
      pageNum: "0",
      quantityPerPage: "20"
    };
    setFilters(newFilters);
    fetchCargos(newFilters, isReleased);
  };
    
    const handleDownload = async (content: Pick<Content, 'id' | 'description'>) => {
      try {
        const response = await downloadContent(content.id);
        
        const downloadUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${content.description}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
          setAlert({ message: `${t('cargo.messages.downloadError')} ${content.description}`, variant: "danger" });
      }
    };

  const columns: GridColDef[] = [
    {
      field: 'visualizar',
      headerName: '',
      width: 80,
      editable: false,
      sortable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      renderCell: ({ row: { id } }: GridRenderCellParams) => (
        <Button variant="secondary" onClick={() => navigate(`cargo/${id}`)}>
          <span className="bi bi-search"></span>
        </Button>
      ),
    },
    { field: 'id', headerName: t('cargo.fields.id'), width: 80 },
    { field: 'fromIdentification', headerName: t('cargo.fields.from'), width: 80 },
    { field: 'clientName', headerName: t('cargo.fields.client'), width: 120 },
    { field: 'supplierName', headerName: t('cargo.fields.supplier'), width: 450 },
    {
      field: 'piecesWeight',
      headerName: t('cargo.fields.piecesWeight'),
      width: 120,
      renderCell: (params: any) => {
        const { pieces, weight, weightRaw } = params.row;
        return `${pieces} / ${weightRaw ?? weight}`;
      },
    },
    { field: 'dawb', headerName: t('cargo.fields.dawb'), width: 100 },
    {
      field: 'pu',
      headerName: t('cargo.fields.pu'),
      type: 'date',
      width: 100,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      },
    },
    { field: 'wrNumber', headerName: t('cargo.fields.wrNumber'), width: 100 },
    {
      field: 'docs',
      headerName: t('cargo.fields.docs'),
      width: 220,
      renderCell: (params: any) => {
        const info = params?.row?.carrierInformation?.[0];
        const invoice = info?.invoice ?? '-';
        const documentKey = info?.document?.key ?? '-';
        return `${invoice} / ${documentKey}`;
      }
    },
    {
      field: 'content',
      headerName: t('cargo.fields.content'),
      width: 500,
      minWidth: 200,
      renderCell: (params: any) => (
        <CargoContentList
          cargoContents={params.row?.cargoContents ?? []}
          onDownload={handleDownload}
          downloadTitle={t('cargo.labels.clickToDownload')}
          expandTitle={t('cargo.labels.showAllFiles')}
        />
      )
    }

  ];

  const navItems: NavBarItem[] = [
    { label: t('cargo.labels.home'), route: '/', iconName: 'house-door' },
    {
      label: t('cargo.labels.newCargoInfo'),
      route: '/cargo-info/create',
      iconName: 'database-add',
    },
    {
      label: t('cargo.labels.releasedList'),
      route: '/cargo-info/',
      iconName: 'table',
      onClick: () => {
        fetchCargoSearchList();
        fetchCargos({
          pageNum: "0",
          quantityPerPage: "20"
        }, true);
        setIsReleased(true);
      },
      isHidden: isReleased,
    },
    {
      label: t('cargo.labels.cargoInfoList'),
      route: '/cargo-info/',
      iconName: 'table',
      onClick: () => {
        fetchCargoSearchList();
        fetchCargos({
          pageNum: "0",
          quantityPerPage: "20"
        }, false);
        setIsReleased(false);
      },
      isHidden: !isReleased,
    },
    {
      label: t('cargo.labels.supplierList'),
      route: '/supplier/',
      iconName: 'info-circle',
    },
  ];

  return (
    <>
      <NavBar items={navItems} />
      {alert && (
        <AlertOverlay
          message={alert.message}
          variant={alert.variant}
          onClose={() => setAlert(null)}
        />
      )}
      <Container fluid style={{ height: 'calc(100vh - 210px)', overflow: 'auto' }}>
        <h2 className="mt-4">
          {isReleased
            ? getPageName('cargoInfoReleased')
            : getPageName('cargoInfo')}
        </h2>
        <Filter
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
        />
        <CargoTablePaginated 
          apiResponse={apiResponse} 
          rows={cargos ?? []} 
          columns={columns} 
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
        />
      </Container>
    </>
  );
};

export default CargoListView;
